<template>
  <ValidationObserver
    ref="radioObserver"
    tag="div" >
    <ValidationProvider
      ref="radioProvider"
      v-slot="{ errors }"
      name="test"
      :rules="validators">
      <div class="flex gap-8 wrap">
        <div
          v-for="(option, index) in options"
          :key="index"
          class="no-shrink option flex flex-align-center"
          :class="{
            selected: selected === option.value,
            'prefix-image': option.image
          }"
          :style="{ 'background-image': option.image ? `url(${option.image})` : '' }"
          @click="selectOption(option.value)" >
          <span class="greedy ellipsis">{{ option.label }}</span>
          <SBIcon
            v-if="(markSelected && selected === option.value) ||
              (!markSelected && markedItems.includes(option.value))"
            :icon="SBIconList.DONE_CIRCLE"
            :color="SBColors.bluePrimary"
            :size="13"/>
        </div>
      </div>
      <div
        v-if="errors[0]"
        class="error flex flex-align-center">
        <SBIcon
          class="mr-1"
          :icon="SBIconList.ERROR"
          :color="SBColors.redPrimary"
          :size="10" />
        <span>{{ errors[0] }}</span>
      </div>
    </ValidationProvider>
  </ValidationObserver>
</template>

<script>
import SBColors from '@/helpers/lists/colors';
import SBIcon from '@/components/common/Icon';
import SBIconList from '@/helpers/lists/sb-icon-list';

export default {
  name: 'RadioButtons',
  components: {
    SBIcon,
  },
  props: {
    value: {
      type: Number,
      required: true,
    },
    /** String list of validators separated by '|'. */
    validators: {
      type: String,
      default: '',
    },
    /**
     * Array of options to be displayed.
     * Each option should have a label and a value.
     * Optionally it can have an image.
     *
     * Example:
     * [
     *    { label: 'Option 1', value: 1 },
     *    { label: 'Option 2', value: 2 },
     *    { label: 'Option 3', value: 3, image: 'https://example.com/image.png' },
     * ]
     */
    options: {
      type: Array,
      default: () => [],
    },
    /** Flag to indicate if the selected option should have a checkmark image next to it. Selected option is always of a different color. */
    markSelected: {
      type: Boolean,
      required: false,
      default: true,
    },
    /**
     * If checked icon should be displayed next to the option based on some other criteria than the selected option.
     * Array values are option values that should have the icon.
     * Example:
     * [1, 2]
     */
    markedItems: {
      type: Array,
      required: false,
      default: () => [],
    },
  },
  data() {
    return {
      SBColors,
      SBIconList,
      selected: null,
    };
  },
  mounted() {
    if (this.value) {
      this.selected = this.value;
      this.$refs.radioProvider.syncValue(this.selected);
      this.emitData();
    }
    setTimeout(() => {
      this.emitData(true);
      this.$refs.radioObserver.validate();
    }, 0);
  },
  methods: {
    selectOption(index) {
      this.selected = index;
      this.$refs.radioProvider.syncValue(this.selected);
      this.emitData();
    },
    emitData(reset = false) {
      if (!this.$refs.radioObserver) return;
      this.$refs.radioObserver.validate({ silent: true })
        .then((valid) => {
          this.$emit('status', {
            valid,
          });
          this.$emit('input', this.selected);
        })
        .finally(() => {
          if (reset) this.$refs.radioObserver.reset();
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.option {
  padding: 9px 12px;
  border-radius: 10px;
  box-shadow: 0 1px 3px 0 rgba(48, 54, 70, 0.1);
  border: solid 1px $greyBorder;
  background-color: $white;
  cursor: pointer;
  flex-basis: calc(50% - 8px); // it is important to have 10px padding which matches the gap between the columns. The last gap will be removed anyway and the columns will stretch due to the flex-grow property.
  flex-grow: 50;
  width: 1px;
  max-height: 34px;

  span {
    font-size: 14px;
    font-weight: 400;
  }

  &.selected {
    background-color: #f0f7ff;
    span {
      font-weight: 500;
    }
  }

  &.prefix-image {
    padding-left: 31px;
    background-repeat: no-repeat;
    background-position: 12px center;
    background-size: 14px 14px;
  }
}
</style>
